import React from 'react';
import { Chart } from 'react-chartjs-2';
import { ClubConditionsData } from '../../types/clubConditions';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface HorizontalRatioBarChartProps {
  data?: ClubConditionsData[];
  title: string;
  description?: string;
  // Computes the percentage value for a given data row
  getValue: (item: ClubConditionsData) => number;
  colorIndex?: number;
}

/**
 * نمودار میله‌ای افقی ساده برای نمایش آزمایشی متریک‌های شروط باشگاه مشتریان.
 * ماه‌ها روی محور عمودی و مقدار درصدی روی محور افقی نمایش داده می‌شوند.
 * بدون خط روند و بدون ChartControls (نسخه سبک/آزمایشی).
 */
const HorizontalRatioBarChart: React.FC<HorizontalRatioBarChartProps> = ({
  data = [],
  title,
  description,
  getValue,
  colorIndex = 0
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);

  // Sort data by year and monthNum in ascending order (same as the vertical charts)
  const sortedData = [...data].sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.monthNum - b.monthNum;
  });

  const chartData = sortedData.map(item => ({
    month: `${item.month}`,
    ratio: getValue(item)
  }));

  // ارتفاع پویا بر اساس تعداد ماه‌ها تا ضخامت میله‌ها و خوانایی برچسب‌ها حفظ شود
  const chartHeight = Math.max(320, chartData.length * 34 + 40);

  const baseOptions = getChartJsOptions(currentTheme);
  const horizontalBarOptions = {
    ...baseOptions,
    indexAxis: 'y' as const,
    scales: {
      ...baseOptions.scales,
      x: {
        ...baseOptions.scales?.x,
        min: 0,
        max: 100,
        ticks: {
          ...baseOptions.scales?.x?.ticks,
          callback: function (value: any) {
            return Number(value).toFixed(2) + '%';
          }
        }
      },
      y: {
        ...baseOptions.scales?.y,
        ticks: {
          ...baseOptions.scales?.y?.ticks,
          autoSkip: false,
          // محور Y اکنون دسته‌بندی (ماه) است؛ برچسب واقعی ماه را نمایش بده
          callback: function (this: any, value: any) {
            return this.getLabelForValue(value);
          }
        }
      }
    },
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        ...baseOptions.plugins?.tooltip,
        callbacks: {
          label: function (context: any) {
            return `${context.parsed.x.toFixed(2)}%`;
          }
        }
      }
    }
  };

  return (
    <div className="card-base card-padding animate-enter animate-delay-3 h-full flex flex-col">
      {/* ارتفاع ثابت سربرگ تا ناحیه رسم همه نمودارها هم‌تراز شوند */}
      <div className="mb-6 h-[5.5rem] overflow-hidden">
        <h3 className="title-card line-clamp-2">{title}</h3>
        {description && (
          <p className="text-text-secondary text-sm mt-1 text-center line-clamp-2">{description}</p>
        )}
      </div>

      <div style={{ height: chartHeight, width: '100%' }}>
        {chartData.length > 0 ? (
          <Chart
            type="bar"
            data={{
              labels: chartData.map(item => item.month),
              datasets: [
                {
                  type: 'bar' as const,
                  label: title,
                  data: chartData.map(item => item.ratio),
                  backgroundColor: getChartBackgroundColor(colorIndex, currentTheme, 0.8),
                  borderColor: chartColors[colorIndex % chartColors.length],
                  borderWidth: 2,
                  borderRadius: 6
                }
              ]
            }}
            options={horizontalBarOptions}
            aria-label={`نمودار میله‌ای افقی ${title}`}
          />
        ) : (
          <div className="flex items-center justify-center p-8 text-text-tertiary text-sm">
            <p>داده‌ای برای نمایش نمودار وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalRatioBarChart;
